import { defineStore } from 'pinia';
import { markRaw } from 'vue';
import {
	type Character,
	defaultCharacter
} from '../models/character';

import { Firestore, collection, doc, getDoc, getDocs, setDoc, deleteDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { type CharacterShareClient } from '../models/characterShare';
import { type CharacterTrashItem } from '../models/characterTrash';
// import * as _ from 'lodash';
import isNil from 'lodash/isNil';
import get from 'lodash/get';
import first from 'lodash/first';
import find from 'lodash/find';
import clone from 'lodash/clone';
import { useUserStore } from './user';
import { useBlueprintStore } from './blueprint';
import { type FeatureOption, type Feature, FeatureType, type FeatureOptions } from '~/models/feature';
import { type Property, PropertyType, property2FeatureOption, meetsRequires, type RequiresContext } from '~/models/property';
import { type Effect, EffectType } from '~/models/effect';
import { useEffectScanner } from '~/composables/useEffectScanner';
import {
	validateCharacter as validateCharacterPure,
	getFeatureSelectionRules as getFeatureSelectionRulesPure,
	getSelectionsForFeature as getSelectionsForFeaturePure,
	type ValidationError,
} from '~/composables/characterValidator';

/**
 * Biology options that auto-grant a mutation feat when selected.
 * Maps biology property ID → feat property ID.
 */
const BIOLOGY_FEAT_GRANTS: Record<string, string> = {
	'biology-mutant-manipulation-arm-tentacle': 'feat-mutant-tentacle',
	'biology-mutant-manipulation-2-tentacles': 'feat-mutant-tentacle',
	'biology-mutant-manipulation-4-tentacles': 'feat-mutant-tentacle',
	'biology-mutant-sensory-stalks': 'feat-mutant-eye-stalks',
};

/** Tag used to mark auto-granted feats so they can be removed when the biology pick changes. */
const AUTO_GRANT_TAG_PREFIX = 'auto-grant:';

export interface CharacterStateInterface {
	character: Character;
	rankFeatureSelected: Feature;
	rankPropertyRank: number;
	selectedFeatureOption: FeatureOption;
	characters: Character[];
	sharedCharacters: CharacterShareClient[];
	trash: CharacterTrashItem[];
	openCharacterDialog: boolean;
	/** Cached bonus scan results — updated once per origin change, read synchronously everywhere. */
	bonusCache: {
		originId: string;
		bonusStat: number;
		bonusFeat: number;
		bonusKnack: number;
	};
}

export const useCharacterStore = defineStore('CharacterStore', {
	state: (): CharacterStateInterface => ({
		character: defaultCharacter(),
		rankFeatureSelected: {} as Feature,
		rankPropertyRank: 0,
		selectedFeatureOption: {} as FeatureOption,
		characters: [],
		sharedCharacters: [],
		trash: [],
		openCharacterDialog: false,
		bonusCache: { originId: '', bonusStat: 0, bonusFeat: 0, bonusKnack: 0 },
	}),

	getters: {
		// Add any specific getters you might need, e.g., items by type
		selectedCharacter: (state) => {
			return state.character;
		},
		characterById: (state) => {
			return (characterId: string): Character | undefined =>
				state.characters.find((character) => character.id === characterId);
		},
		characterOriginName: (state) => {
			if (state.character.details.length > 0) {
				const feature = state.character.details[0].features.find((item: FeatureOption) => item.type === FeatureType.origin);
				if(feature != undefined) {
					return feature.name;
				}
			}
		},
		characterCoreName: (state): string | undefined => {
			for (const rankDetails of state.character.details) {
				const feature = (rankDetails?.features ?? []).find(
					(item: FeatureOption) => item.type === FeatureType.core
				);
				if (feature) return feature.name;
			}
		},
		characterOriginTag: (state): string | undefined => {
			if (state.character.details.length > 0) {
				const feature = state.character.details[0].features.find(
					(item: FeatureOption) => item.type === FeatureType.origin
				);
				return feature?.tag;
			}
		},
		/**
		 * Build the full set of tags the character currently has.
		 * Includes: rank tags (rank0..character.rank), feature tags,
		 * and core class ID tag (e.g. "merc" from "core-merc").
		 */
		characterTags: (state): Set<string> => {
			const tags = new Set<string>();

			// Add rank tags up to the character's rank (not details.length)
			for (let r = 0; r <= state.character.rank; r++) {
				tags.add(`rank${r}`);
			}

			// Add feature tags from all detail entries
			for (const detail of state.character.details) {
				for (const feature of detail?.features ?? []) {
					if (feature.tag) {
						tags.add(feature.tag);
					}
					// Core features: also add the class-specific tag from the ID
					// e.g. core-merc → "merc", core-sci-ops → "sci-ops"
					if (feature.type === FeatureType.core && feature.id?.startsWith('core-')) {
						tags.add(feature.id.replace('core-', ''));
					}
				}
			}

			return tags;
		},
		getKnackFeatureOptions: (state): FeatureOptions => {
			if (state.character.details.length > 0) {
				const propertyStore = usePropertyStore();
				const knacks = propertyStore.propertiesByType(PropertyType.knack);
				const store = useCharacterStore();
				const tags = store.characterTags;

				const applicableKnacks = knacks.filter((knack: Property) =>
					meetsRequires(knack.requires, tags)
				);

				return applicableKnacks
					.sort((a, b) => a.name.localeCompare(b.name))
					.map((knack) => property2FeatureOption(knack));
			}
			return [];
		},
		getSkillBoonFeatureOptions: (state): FeatureOptions => {
			if (state.character.details.length > 0) {
				const propertyStore = usePropertyStore();
				const skillBoosts = propertyStore.propertiesByType(PropertyType.skillBoon);
				const store = useCharacterStore();
				const tags = store.characterTags;

				const applicableBoosts = skillBoosts.filter((boost: Property) =>
					meetsRequires(boost.requires, tags)
				);

				return applicableBoosts
					.sort((a, b) => a.name.localeCompare(b.name))
					.map((boost) => property2FeatureOption(boost));
			}
			return [];
		},
		getFeatFeatureOptions: (state): FeatureOptions => {
			if (state.character.details.length > 0) {
				const propertyStore = usePropertyStore();
				const feats = propertyStore.propertiesByType(PropertyType.feat);
				const store = useCharacterStore();
				const tags = store.characterTags;

				return feats.filter((feat: Property) => meetsRequires(feat.requires, tags))
					.sort((a, b) => a.name.localeCompare(b.name))
					.map((feat) => property2FeatureOption(feat));
			}
			return [];
		},
		getTalentFeatureOptions: (state): FeatureOptions => {
			if (state.character.details.length > 0) {
				const propertyStore = usePropertyStore();
				const talents = propertyStore.propertiesByType(PropertyType.talent);
				const store = useCharacterStore();
				const tags = store.characterTags;

				return talents.filter((talent: Property) => meetsRequires(talent.requires, tags))
					.sort((a, b) => a.name.localeCompare(b.name))
					.map((talent) => property2FeatureOption(talent));
			}
			return [];
		},
		getLegacyItemFeatureOptions: (state): FeatureOptions => {
			if (state.character.details.length > 0) {
				const propertyStore = usePropertyStore();
				const legacyItems = propertyStore.propertiesByType(PropertyType.legacyItem);
				return legacyItems
					.sort((a, b) => a.name.localeCompare(b.name))
					.map((item) => property2FeatureOption(item));
			}
			return [];
		},
		isCharacterValid: (state): boolean => {
			const store = useCharacterStore();
			const errors = store.validateCharacter();
			return errors.length === 0;
		},
		getFeatureValidationState: (state) => {
			return (featureType: FeatureType, rank: number): { isValid: boolean, error?: string } => {
				const store = useCharacterStore();
				const errors = store.validateCharacter();
				const error = errors.find(e => e.rank === rank && e.featureType === featureType);
				return {
					isValid: !error,
					error: error?.message
				};
			};
		},
		getFeatureSelectionCount: (state) => {
			return (feature: Feature, rank: number): { selected: number, total?: number } => {
				const store = useCharacterStore();

				// For skills, count total qty + trick costs (not entry count)
				if (feature.type === FeatureType.skill || feature.type === FeatureType.skillTrick) {
					const selected = store.getSkillPointsAllocatedAtRank(rank);
					return { selected, total: undefined };
				}

				// For stats, count total qty (not entry count)
				if (feature.type === FeatureType.stats) {
					const selected = store.getStatPointsAllocatedAtRank(rank);
					return { selected, total: undefined };
				}

				// Count how many of this type are selected
				const selected = state.character.details[rank]?.features.filter(
					(f: FeatureOption) => f.type === feature.type
				).length || 0;

				// Get the requirement (num, max, or min)
				const rules = store.getFeatureSelectionRules(feature);
				const total = rules.num ?? rules.max ?? rules.min;

				return { selected, total };
			};
		},
		/**
		 * Get selections for a specific feature at a rank.
		 * When the feature has memberRefs, scope the match to those property IDs —
		 * necessary when siblings share a type (e.g. Mutant Biology's Mass/Locomotion/
		 * Manipulation/Sensory are all type=biology, so a type-only filter would
		 * surface every biology pick under every sub-feature).
		 */
		getSelectionsForFeature: (state) => {
			return (feature: Feature, rank: number): FeatureOption[] => {
				// Delegate to the shared composable so the memberRefs edge case
				// stays identical across Pinia / Cloud Functions / Foundry.
				return getSelectionsForFeaturePure(
					feature as unknown as Parameters<typeof getSelectionsForFeaturePure>[0],
					state.character.details[rank] as unknown as Parameters<typeof getSelectionsForFeaturePure>[1],
				) as unknown as FeatureOption[];
			};
		},
		/**
		 * Get total skill points allocated at a rank (sum of skill qty + trick SP costs)
		 */
		getSkillPointsAllocatedAtRank: (state) => {
			return (rank: number): number => {
				const features = state.character.details[rank]?.features || [];

				const skillSP = features
					.filter((f: FeatureOption) => f.type === FeatureType.skill)
					.reduce((sum, f) => sum + (f.qty || 0), 0);

				const trickSP = features
					.filter((f: FeatureOption) => f.type === FeatureType.skillTrick)
					.reduce((sum, f) => {
						const match = f.description?.match(/Cost:\s*(\d+)\s*Skill\s*points?/i);
						return sum + (match ? parseInt(match[1]) : 0);
					}, 0);

				return skillSP + trickSP;
			};
		},
		/**
		 * Get total stat points allocated at a rank (sum of stat qty values)
		 */
		getStatPointsAllocatedAtRank: (state) => {
			return (rank: number): number => {
				const features = state.character.details[rank]?.features || [];
				return features
					.filter((f: FeatureOption) => f.type === FeatureType.stats)
					.reduce((sum, f) => sum + (f.qty || 0), 0);
			};
		}
	},

	actions: {
		/**
		 * Build a RequiresContext for threshold-based requires (e.g. skill-id>=5).
		 * Sums cumulative SP across all ranks for a given skill ID.
		 */
		buildRequiresContext(): RequiresContext {
			return {
				getSkillSP: (skillId: string): number => {
					let total = 0;
					for (let r = 0; r < this.character.details.length; r++) {
						const f = this.character.details[r]?.features?.find(
							(f: FeatureOption) => f.id === skillId && f.type === FeatureType.skill
						);
						if (f) total += f.qty || 0;
					}
					return total;
				}
			};
		},

		/**
		 * Check if character has a specific property/feature selected
		 * @param propertyId The property or feature ID to check for
		 * @param rank The character rank
		 * @returns true if the character has this property selected
		 */
		hasProperty(propertyId: string, rank: number): boolean {
			// Check if it's in the character's features at or before this rank
			for (let r = 0; r <= rank; r++) {
				if (this.character.details[r]?.features?.some((f: FeatureOption) => f.id === propertyId)) {
					return true;
				}
			}
			return false;
		},


		async set(character: Character) {
			const userStore = useUserStore();
			if (!isNil(character)) {
				const storeCharacter = clone(character);
				this.openCharacterDialog = false;
				this.select(storeCharacter);
				// await this.fetch(storeCharacter);
			} else {
				this.select(defaultCharacter());
			}
		},
		getKnackOptions2() {
			if (this.character.details.length > 0) {
				const propertyStore = usePropertyStore();
				const knacks = propertyStore.propertiesByType(PropertyType.knack);
				const tags = this.characterTags;
				return knacks.filter((item: Property) => meetsRequires(item.requires, tags));
			}
			return [];
		},
		async fetch(character: Character) {
			let char = defaultCharacter();
			const userStore = useUserStore();			
			if (userStore.isLoggedIn && character.id) {
				const { $firestore } = useNuxtApp();
				const docRef = doc($firestore as Firestore, `/users/${userStore.id}/characters/`, character.id);
				const docCharacter = await getDoc(docRef);
				if (!isNil(docCharacter.data())) {
					char = { ...defaultCharacter(), id: docCharacter.id, ...docCharacter.data() } as Character
					this.add(char);
					this.select(char);
				} 
			}
		},
		async fetchAll(character?: Character) {
			const userStore = useUserStore();
			if (userStore.isLoggedIn) {
				const { $firestore } = useNuxtApp();
				const querySnapshot = await getDocs(collection($firestore as Firestore, `/users/${userStore.id}/characters`));
				this.characters = querySnapshot.docs.map((doc) => {
					const data = doc.data();
					// Strip derived `properties` arrays — they may contain hydrated
					// Property objects with circular DocumentReference instances from
					// pre-fix saves. Properties are rebuilt from features at runtime.
					if (data.details) {
						for (const d of data.details) {
							if (d) d.properties = [];
						}
					}
					return { ...defaultCharacter(), id: doc.id, ...data } as Character;
				});
				return this.characters;
			}
		},
		add(char: Character) {
			if (!this.characterById(char.id)) {
				this.characters.push(char);
			}
		},
		new() {
			this.character = defaultCharacter();
		},
		async delete(char: Character) {
			const userStore = useUserStore();
			if (userStore.isLoggedIn && char.id) {
				try {
					const functions = getFunctions();
					const deleteChar = httpsCallable<{ characterId: string }, { success: boolean }>(functions, 'deleteCharacter');
					await deleteChar({ characterId: char.id });
					this.new();
					await this.fetchAll();
					await this.fetchTrash();
				} catch (error) {
					console.error('Error deleting Character: ', error);
				}
			}
		},
		async fetchSharedCharacters() {
			const userStore = useUserStore();
			if (!userStore.isLoggedIn) return;

			const { $firestore } = useNuxtApp();
			const snap = await getDocs(
				collection($firestore as Firestore, `users/${userStore.id}/character-shares`)
			);

			this.sharedCharacters = snap.docs.map(d => ({
				shareId: d.id,
				characterId: d.data().characterId,
				ownerAlias: d.data().ownerAlias,
				characterName: d.data().characterName,
				sharedAt: d.data().sharedAt,
			} as CharacterShareClient));
		},
		async loadSharedCharacter(shareId: string): Promise<Character | null> {
			try {
				const functions = getFunctions();
				const getShared = httpsCallable<{ shareId: string }, { character: Character; ownerAlias: string; characterId: string }>(functions, 'getSharedCharacter');
				const result = await getShared({ shareId });
				return result.data.character;
			} catch (error) {
				console.error('Error loading shared character:', error);
				return null;
			}
		},
		async fetchTrash() {
			const userStore = useUserStore();
			if (!userStore.isLoggedIn) return;

			const { $firestore } = useNuxtApp();
			const snap = await getDocs(
				collection($firestore as Firestore, `users/${userStore.id}/character-trash`)
			);

			this.trash = snap.docs.map(d => ({
				id: d.id,
				...d.data(),
			} as CharacterTrashItem));
		},
		async emptyTrash() {
			try {
				const functions = getFunctions();
				const empty = httpsCallable<object, { success: boolean; count: number }>(functions, 'emptyTrash');
				await empty({});
				this.trash = [];
			} catch (error) {
				console.error('Error emptying trash:', error);
			}
		},
		async restoreFromTrash(trashId: string) {
			const userStore = useUserStore();
			if (!userStore.isLoggedIn) return;

			const { $firestore } = useNuxtApp();
			const trashDoc = await getDoc(
				doc($firestore as Firestore, `users/${userStore.id}/character-trash/${trashId}`)
			);
			if (!trashDoc.exists()) return;

			const trashData = trashDoc.data() as CharacterTrashItem;
			const character = trashData.character;
			character.id = '-1'; // force new doc creation
			character.lastSaved = undefined;

			this.add(character);
			this.select(character);
			await this.upsert(character);

			// Delete from trash
			await deleteDoc(doc($firestore as Firestore, `users/${userStore.id}/character-trash/${trashId}`));
			this.trash = this.trash.filter(t => t.id !== trashId);
		},
		select(char: Character) {
			this.character = clone(char);
			// Invalidate bonus cache — CharacterEditor will refresh it after
			// the bundle/effects are hydrated. Calling refreshBonusCache() here
			// would trigger individual Firestore reads before the bundle loads.
			this.bonusCache = { originId: '', bonusStat: 0, bonusFeat: 0, bonusKnack: 0 };
		},
		async upsert(char: Character) {
			console.log(`CharacterStore upsertItem: ${char.id}`);
			const userStore = useUserStore();
			if (userStore.isLoggedIn) {
				// If name changed on an existing character, save as new copy
				const existing = this.characterById(char.id);
				if (existing && char.id !== '-1' && existing.name !== char.name) {
					console.log(`Name changed from "${existing.name}" to "${char.name}" — creating new copy`);
					char.id = '-1';
				}

				// Stamp save timestamp
				const now = new Date();
				const pad = (n: number) => String(n).padStart(2, '0');
				char.lastSaved = `${pad(now.getMonth() + 1)}/${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}`;

				// Fully hydrate all properties with effects before saving
				console.log('Hydrating character properties with effects...');
				await this.hydrateCharacterProperties(char);

				const { $firestore } = useNuxtApp();
				const nodesRef = collection($firestore as Firestore, `/users/${userStore.id}/characters`)
				let nodeRef = doc(nodesRef);
				if (char.id == '-1' || char.id == undefined) {
					char.id = nodeRef.id;
					console.log(`noderef.id ${char.id} => ${nodeRef.id}`);
				} else {
					console.log(`item.id ${char.id}`);
					nodeRef = doc(nodesRef, char.id);
				}
				// Strip the derived `properties` array from each rank detail before
				// saving — it contains hydrated Property objects with Firestore
				// DocumentReference instances (circular via ._firestore) that blow
				// up the encoder. Properties are rebuilt from features at runtime.
				const strippedDetails = (char.details || []).map(d => ({
					features: d.features || [],
					properties: [],
				}));

				// Strip Vue reactivity proxies and undefined values before writing —
				// Firestore rejects Proxy objects. The blueprint store does the same
				// (JSON.parse/stringify round-trip in upsertFeature).
				const charToSave = JSON.parse(JSON.stringify({ ...char, details: strippedDetails }));

				return await setDoc(nodeRef, charToSave)
					.then(async () => {
						this.select(char);
						await this.fetchAll();
						console.log('Character saved with fully hydrated properties');
						return char;
					})
					.catch((error) => {
						console.log('Error saving Character: ', error);
					});
			}
		},

		/**
		 * Hydrate all properties in a character with their effects
		 * Ensures all properties have their effects loaded before saving
		 */
		async hydrateCharacterProperties(char: Character) {
			const propertyStore = usePropertyStore();

			for (let rank = 0; rank < char.details.length; rank++) {
				if (char.details[rank] && char.details[rank].properties) {
					for (const property of char.details[rank].properties) {
						if (property) {
							await propertyStore.loadEffects(property);
						}
					}
				}
			}

			console.log(`✓ Hydrated properties for ${char.details.length} ranks`);
		},
		selectRankProperty(rankProperty: Feature, rank: number) {
			// markRaw prevents Vue/DevTools from deep-proxying the Feature object,
			// which contains Firestore DocumentReferences with circular ._firestore
			// back-refs that blow up the devtools serializer.
			this.rankFeatureSelected = markRaw(rankProperty);
			this.rankPropertyRank = rank;
		},
		selectPropertyOption(propertyOption: FeatureOption, rank: number) {
			this.selectedFeatureOption = propertyOption;
			this.setCharacterFeatureOption(propertyOption, rank);
		},
		/**
		 * Allocate a skill point to a skill at a given rank.
		 * If the skill already has an entry, increment qty. Otherwise create with qty=1.
		 */
		allocateSkillPoint(featureOption: FeatureOption, rank: number) {
			// Ensure details array is initialized
			while (this.character.details.length <= rank) {
				this.character.details.push({ features: [], properties: [] });
			}

			const existing = this.character.details[rank].features.find(
				(f: FeatureOption) => f.id === featureOption.id && f.type === FeatureType.skill
			);

			if (existing) {
				existing.qty = (existing.qty || 0) + 1;
			} else {
				this.character.details[rank].features.push({
					...featureOption,
					type: FeatureType.skill,
					qty: 1,
				});
			}
		},
		/**
		 * Purchase a skill trick at a given rank.
		 * Stores as a FeatureType.skillTrick entry. SP cost is parsed from description.
		 */
		purchaseSkillTrick(featureOption: FeatureOption, rank: number) {
			while (this.character.details.length <= rank) {
				this.character.details.push({ features: [], properties: [] });
			}

			// Don't allow duplicates
			const existing = this.character.details[rank].features.find(
				(f: FeatureOption) => f.id === featureOption.id && f.type === FeatureType.skillTrick
			);
			if (existing) return;

			this.character.details[rank].features.push({
				...featureOption,
				type: FeatureType.skillTrick,
				qty: 1,
			});
		},
		/**
		 * Remove a purchased skill trick at a given rank.
		 */
		unpurchaseSkillTrick(trickId: string, rank: number) {
			if (!this.character.details[rank]) return;

			const index = this.character.details[rank].features.findIndex(
				(f: FeatureOption) => f.id === trickId && f.type === FeatureType.skillTrick
			);
			if (index !== -1) {
				this.character.details[rank].features.splice(index, 1);
			}
		},
		/**
		 * Purchase a core ability at a given rank.
		 * Stores as a FeatureType.coreAbility entry. AP cost is parsed from description.
		 */
		purchaseAbility(featureOption: FeatureOption, rank: number) {
			while (this.character.details.length <= rank) {
				this.character.details.push({ features: [], properties: [] });
			}

			// Don't allow duplicates
			const existing = this.character.details[rank].features.find(
				(f: FeatureOption) => f.id === featureOption.id && f.type === FeatureType.coreAbility
			);
			if (existing) return;

			this.character.details[rank].features.push({
				...featureOption,
				type: FeatureType.coreAbility,
				qty: 1,
			});
		},
		/**
		 * Remove a purchased core ability at a given rank.
		 */
		unpurchaseAbility(abilityId: string, rank: number) {
			if (!this.character.details[rank]) return;

			const index = this.character.details[rank].features.findIndex(
				(f: FeatureOption) => f.id === abilityId && f.type === FeatureType.coreAbility
			);
			if (index !== -1) {
				this.character.details[rank].features.splice(index, 1);
			}
		},
		/**
		 * Allocate a stat point to a stat at a given rank.
		 * Uses the +1 stat property with qty, same pattern as skills.
		 */
		allocateStatPoint(featureOption: FeatureOption, rank: number) {
			while (this.character.details.length <= rank) {
				this.character.details.push({ features: [], properties: [] });
			}

			const existing = this.character.details[rank].features.find(
				(f: FeatureOption) => f.id === featureOption.id && f.type === FeatureType.stats
			);

			if (existing) {
				existing.qty = (existing.qty || 0) + 1;
			} else {
				this.character.details[rank].features.push({
					...featureOption,
					type: FeatureType.stats,
					qty: 1,
				});
			}
		},
		/**
		 * Deallocate a stat point from a stat at a given rank.
		 * If qty > 1, decrement. If qty === 1, remove the entry.
		 */
		deallocateStatPoint(statId: string, rank: number) {
			if (!this.character.details[rank]) return;

			const index = this.character.details[rank].features.findIndex(
				(f: FeatureOption) => f.id === statId && f.type === FeatureType.stats
			);
			if (index === -1) return;

			const feature = this.character.details[rank].features[index];
			if ((feature.qty || 0) > 1) {
				feature.qty = (feature.qty || 0) - 1;
			} else {
				this.character.details[rank].features.splice(index, 1);
			}
		},
		/**
		 * Allocate a sense point to a sense at a given rank.
		 * Each sense's qty = tier cost (0=None, 1=Poor, 2=Average, 3=Good, 4=Excellent).
		 */
		allocateSensePoint(featureOption: FeatureOption, rank: number) {
			while (this.character.details.length <= rank) {
				this.character.details.push({ features: [], properties: [] });
			}

			const existing = this.character.details[rank].features.find(
				(f: FeatureOption) => f.id === featureOption.id && f.type === FeatureType.senses
			);

			if (existing) {
				existing.qty = (existing.qty || 0) + 1;
			} else {
				this.character.details[rank].features.push({
					...featureOption,
					type: FeatureType.senses,
					qty: 1,
				});
			}
		},
		/**
		 * Deallocate a sense point from a sense at a given rank.
		 */
		deallocateSensePoint(senseId: string, rank: number) {
			if (!this.character.details[rank]) return;

			const index = this.character.details[rank].features.findIndex(
				(f: FeatureOption) => f.id === senseId && f.type === FeatureType.senses
			);
			if (index === -1) return;

			const feature = this.character.details[rank].features[index];
			if ((feature.qty || 0) > 1) {
				feature.qty = (feature.qty || 0) - 1;
			} else {
				this.character.details[rank].features.splice(index, 1);
			}
		},
		/**
		 * Deallocate a skill point from a skill at a given rank.
		 * If qty > 1, decrement. If qty === 1, remove the entry.
		 */
		deallocateSkillPoint(skillId: string, rank: number) {
			if (!this.character.details[rank]) return;

			const index = this.character.details[rank].features.findIndex(
				(f: FeatureOption) => f.id === skillId && f.type === FeatureType.skill
			);
			if (index === -1) return;

			const feature = this.character.details[rank].features[index];
			if ((feature.qty || 0) > 1) {
				feature.qty = (feature.qty || 0) - 1;
			} else {
				this.character.details[rank].features.splice(index, 1);
			}
		},
		/**
		 * Refresh the bonusStat/bonusFeat cache by scanning the origin's effects.
		 * Called once when the origin changes — all consumers read from the cache
		 * synchronously instead of re-scanning per watcher cycle.
		 */
		async refreshBonusCache(rank: number = 0) {
			const origin = this.character.details[0]?.features?.find(
				(f: FeatureOption) => f.type === FeatureType.origin
			);
			const originId = origin?.id ?? '';

			// Skip if already cached for this origin
			if (this.bonusCache.originId === originId && originId !== '') return;

			const { scanFlatBonusForRank } = useEffectScanner();
			const [bonusStat, bonusFeat, bonusKnack] = await Promise.all([
				scanFlatBonusForRank(rank, this.character, EffectType.bonusStat),
				scanFlatBonusForRank(rank, this.character, EffectType.bonusFeat),
				scanFlatBonusForRank(rank, this.character, EffectType.bonusKnack),
			]);

			this.bonusCache = { originId, bonusStat, bonusFeat, bonusKnack };
			console.log(`[BonusCache] Refreshed for origin ${originId}: stat=${bonusStat}, feat=${bonusFeat}, knack=${bonusKnack}`);
		},
		/**
		 * Sync auto-granted feats based on current biology selections at a rank.
		 * Idempotent: computes the desired set, removes stale grants, adds missing ones.
		 */
		syncAutoGrantedFeats(rank: number) {
			if (!this.character.details[rank]) return;
			const features = this.character.details[rank].features;
			const propertyStore = usePropertyStore();

			// Compute which feat IDs should be auto-granted from current biology picks
			const desiredFeatIds = new Set<string>();
			for (const f of features) {
				if (f.type === FeatureType.biology) {
					const grantId = BIOLOGY_FEAT_GRANTS[f.id];
					if (grantId) desiredFeatIds.add(grantId);
				}
			}

			// Remove stale auto-granted feats (tag starts with AUTO_GRANT_TAG_PREFIX)
			for (let i = features.length - 1; i >= 0; i--) {
				const f = features[i];
				if (f.type === FeatureType.feat && f.tag?.startsWith(AUTO_GRANT_TAG_PREFIX)) {
					const grantedFeatId = f.tag.slice(AUTO_GRANT_TAG_PREFIX.length);
					if (!desiredFeatIds.has(f.id)) {
						features.splice(i, 1);
						console.log(`[AutoGrant] Removed stale auto-granted feat: ${f.name}`);
					} else {
						// Already present and still desired — don't re-add
						desiredFeatIds.delete(f.id);
					}
				}
			}

			// Add missing auto-granted feats
			for (const featId of desiredFeatIds) {
				// Don't add if manually picked already (e.g., user spent a feat slot on it)
				const alreadyPicked = features.some(f => f.id === featId && f.type === FeatureType.feat);
				if (alreadyPicked) continue;

				const prop = propertyStore.propertyById(featId);
				if (!prop) {
					console.warn(`[AutoGrant] Feat property ${featId} not found in store`);
					continue;
				}
				const opt = property2FeatureOption(prop);
				// Mark as auto-granted via the tag field so we can distinguish from manual picks
				opt.tag = `${AUTO_GRANT_TAG_PREFIX}${featId}`;
				features.push(opt);
				console.log(`[AutoGrant] Added auto-granted feat: ${opt.name}`);
			}
		},
		/**
		 * Thin wrapper around the framework-free helper in
		 * composables/characterValidator.ts. The shared composable is the
		 * single source of truth for min/max/num derivation.
		 */
		getFeatureSelectionRules(feature: Feature): { min?: number, max?: number, num?: number } {
			return getFeatureSelectionRulesPure(feature as unknown as Parameters<typeof getFeatureSelectionRulesPure>[0]);
		},
		async setCharacterFeatureOption(featureOption: FeatureOption, rank: number) {
			if (!featureOption) {
				console.error(`setCharacterFeatureOption feature ${JSON.stringify(featureOption)} NOT FOUND`);
				return;
			}

			// Skills, skill tricks, stats, and senses use their own allocate/deallocate methods
			if (featureOption.type === FeatureType.skill || featureOption.type === FeatureType.skillTrick || featureOption.type === FeatureType.stats || featureOption.type === FeatureType.senses) {
				return;
			}

			// Ensure details array is initialized
			while (this.character.details.length <= rank) {
				this.character.details.push({
					features: [],
					properties: [],
				});
			}

			// Use feature selectionRules from blueprints (migrated from PropertyGroups)
			console.log(`[CharacterStore] Using feature selection for ${featureOption.name} (type: ${featureOption.type})`);
			const blueprintStore = useBlueprintStore();
			const blueprintFeatures = blueprintStore.getCharacterFeaturesForRank(rank);

			// Prefer the exact sub-feature the user clicked in the tree
			// (rankFeatureSelected). This is critical for Mutant Biology where
			// siblings share type='biology' — a type-based lookup would match
			// the parent container first and the per-sub-feature cap would
			// never run.
			let blueprintFeature: Feature | undefined;
			let parentFeature: Feature | undefined;

			const selected = this.rankFeatureSelected;
			if (selected && selected.type === featureOption.type) {
				blueprintFeature = selected;
				// Find the parent of the selected sub-feature in the blueprint
				for (const bf of blueprintFeatures) {
					if (bf.id === selected.id) break; // selected is itself top-level
					if (bf.subFeatures?.some(sf => sf.id === selected.id)) {
						parentFeature = bf;
						break;
					}
					for (const sf of bf.subFeatures ?? []) {
						if (sf.subFeatures?.some(nsf => nsf.id === selected.id)) {
							parentFeature = sf;
							break;
						}
					}
					if (parentFeature) break;
				}
			} else {
				// Fallback: type-based lookup (used for features not driven by
				// a tree click, e.g. programmatic selection).
				for (const bf of blueprintFeatures) {
					if (bf.type === featureOption.type) {
						blueprintFeature = bf;
						break;
					}
					for (const sf of bf.subFeatures) {
						if (sf.type === featureOption.type) {
							blueprintFeature = sf;
							parentFeature = bf;
							break;
						}
						const nested = sf.subFeatures?.find(nsf => nsf.type === featureOption.type);
						if (nested) {
							blueprintFeature = nested;
							parentFeature = sf;
							break;
						}
					}
					if (blueprintFeature) break;
				}
			}

			if (!blueprintFeature) {
				console.error(`No blueprint feature found for type ${featureOption.type} at rank ${rank}`);
				return;
			}

			const rules = this.getFeatureSelectionRules(blueprintFeature);
			console.log(`[CharacterStore] Selection rules for ${blueprintFeature.name}:`, rules);
			console.log(`[CharacterStore] Blueprint feature:`, blueprintFeature);

			// Apply cached bonus to grow the effective max. The cache is refreshed
			// once per origin change (not per click), eliminating the async
			// scanFlatBonusForRank calls that were causing GC pressure.
			const bonus = featureOption.type === FeatureType.feat ? this.bonusCache.bonusFeat
				: featureOption.type === FeatureType.stats ? this.bonusCache.bonusStat
				: featureOption.type === FeatureType.knack ? this.bonusCache.bonusKnack
				: 0;
			if (bonus > 0) {
				if (rules.max !== undefined) rules.max += bonus;
				if (rules.num !== undefined) {
					rules.max = (rules.num ?? 0) + bonus;
					rules.min = rules.num;
					rules.num = undefined;
				}
				console.log(`[CharacterStore] +${bonus} bonus applied from cache → rules:`, rules);
			}

			const currentSelections = this.character.details[rank].features.filter(
				(f: FeatureOption) => f.type === featureOption.type
			);
			console.log(`[CharacterStore] Current selections of type ${featureOption.type}:`, currentSelections.length);

			// Check if already selected
			const alreadySelected = currentSelections.find((f: FeatureOption) => f.id === featureOption.id);

			if (alreadySelected) {
				// Deselect if clicking already selected item
				const index = this.character.details[rank].features.findIndex(
					(f: FeatureOption) => f.id === featureOption.id
				);
				this.character.details[rank].features.splice(index, 1);
				// Re-sync auto-granted feats after deselecting a biology option
				if (featureOption.type === FeatureType.biology) {
					this.syncAutoGrantedFeats(rank);
				}
			} else {
				// Shared budget applies only when the parent is a PURE CONTAINER
				// (no memberRefs of its own). Legacy Choices and Mutant Biology are
				// containers whose selectionRules represent a budget shared across
				// children. Origin is NOT a container — it has its own memberRefs for
				// picking one of N origins, and its selectionRules.max refers to that
				// pick, not to a shared budget across Origin Feat + homeWorld.
				const parentHasMembers = (parentFeature?.memberRefs && parentFeature.memberRefs.length > 0)
					|| (parentFeature?._memberRefIds && parentFeature._memberRefIds.length > 0);
				const parentIsSharedContainer =
					!!parentFeature &&
					parentFeature.subFeatures.length > 0 &&
					!parentHasMembers;

				// Per-sub-feature limit: only applies inside a shared-budget container
				// where siblings share a type (Mutant Biology). Outside that context
				// (e.g. Origin Feat) the legacy rules path already handles FIFO, and
				// running the sub-cap there causes double-removal.
				const activeSubFeature = this.rankFeatureSelected;
				const activeSubHasMembers = (activeSubFeature?.memberRefs && activeSubFeature.memberRefs.length > 0)
					|| (activeSubFeature?._memberRefIds && activeSubFeature._memberRefIds.length > 0);
				if (
					parentIsSharedContainer &&
					activeSubFeature &&
					activeSubFeature.type === featureOption.type &&
					activeSubHasMembers
				) {
					const subMemberIds = new Set<string>();
					// Bundle path: use _memberRefIds directly
					if (activeSubFeature._memberRefIds?.length) {
						for (const id of activeSubFeature._memberRefIds) {
							subMemberIds.add(id);
						}
					} else if (activeSubFeature.memberRefs) {
						for (const ref of activeSubFeature.memberRefs as any[]) {
							const id = ref?.id
								?? ref?._key?.path?.segments?.slice(-1)?.[0]
								?? ref?._path?.segments?.slice(-1)?.[0];
							if (id) subMemberIds.add(id);
						}
					}
					const subRules = this.getFeatureSelectionRules(activeSubFeature);
					// Apply origin bonuses to sub-feature cap
					const subBonus = featureOption.type === FeatureType.knack ? this.bonusCache.bonusKnack
						: featureOption.type === FeatureType.feat ? this.bonusCache.bonusFeat
						: 0;
					let subMax = subRules.num ?? subRules.max;
					if (subMax !== undefined) subMax += subBonus;
					if (subMax !== undefined) {
						const subSelections = this.character.details[rank].features.filter(
							(f: FeatureOption) => subMemberIds.has(f.id)
						);
						while (subSelections.length >= subMax) {
							const oldest = subSelections.shift();
							if (!oldest) break;
							const oldestIndex = this.character.details[rank].features.findIndex(
								(f: FeatureOption) => f.id === oldest.id
							);
							if (oldestIndex !== -1) {
								this.character.details[rank].features.splice(oldestIndex, 1);
								console.log(`[CharacterStore] Sub-feature cap: removed ${oldest.name} from ${activeSubFeature.name}`);
							}
						}
					}
				}

				if (parentIsSharedContainer) {
					const parentRules = this.getFeatureSelectionRules(parentFeature!);
					// Apply origin bonuses to parent shared budget
					const parentBonus = featureOption.type === FeatureType.knack ? this.bonusCache.bonusKnack
						: featureOption.type === FeatureType.feat ? this.bonusCache.bonusFeat
						: 0;
					const parentBudget = ((parentRules.max ?? parentRules.num) ?? 0) + parentBonus || undefined;
					const childTypes = new Set(parentFeature.subFeatures.map(sf => sf.type));
					const combinedSelections = this.character.details[rank].features.filter(
						(f: FeatureOption) => childTypes.has(f.type)
					);
					console.log(`[CharacterStore] Shared budget: parent=${parentFeature.name}, budget=${parentBudget}, childTypes=${[...childTypes]}, combined=${combinedSelections.length}`);

					if (parentBudget !== undefined && combinedSelections.length >= parentBudget) {
						// FIFO: remove oldest selection from any child type
						const oldest = combinedSelections[0];
						const oldestIndex = this.character.details[rank].features.findIndex(
							(f: FeatureOption) => f.id === oldest.id
						);
						if (oldestIndex !== -1) {
							this.character.details[rank].features.splice(oldestIndex, 1);
							console.log(`[CharacterStore] Shared budget: removed ${oldest.name} (${oldest.type}) to make room`);
						}
					}

					this.character.details[rank].features.push(featureOption);
					console.log(`[CharacterStore] Added ${featureOption.name} to shared budget`);

					// Sync auto-granted feats for biology selections
					if (featureOption.type === FeatureType.biology) {
						this.syncAutoGrantedFeats(rank);
					}

				// Special handling for stats: count point values, not just selections
				} else if (featureOption.type === FeatureType.stats) {
					// Extract point value from stat name (e.g., "Agility 2" -> 2)
					const match = featureOption.name.match(/\s+(\d+)$/);
					const newStatPoints = match ? parseInt(match[1]) : 0;

					// Calculate current points allocated
					const currentPoints = currentSelections.reduce((total, sel) => {
						const match = sel.name.match(/\s+(\d+)$/);
						const points = match ? parseInt(match[1]) : 0;
						return total + points;
					}, 0);

					console.log(`[CharacterStore] Stats: current=${currentPoints}, new=${newStatPoints}, max=${rules.max}`);

					// Remove old selections until we have room for the new one
					if (rules.max !== undefined) {
						let pointsAfterAdd = currentPoints + newStatPoints;
						while (pointsAfterAdd > rules.max && currentSelections.length > 0) {
							// Remove oldest selection (FIFO)
							const oldestStat = currentSelections[0];
							const oldestIndex = this.character.details[rank].features.findIndex(
								(f: FeatureOption) => f.id === oldestStat.id
							);
							if (oldestIndex !== -1) {
								this.character.details[rank].features.splice(oldestIndex, 1);
								const match = oldestStat.name.match(/\s+(\d+)$/);
								const removedPoints = match ? parseInt(match[1]) : 0;
								pointsAfterAdd -= removedPoints;
								console.log(`[CharacterStore] Removed ${oldestStat.name} (${removedPoints} points) to make room`);
								// Update currentSelections
								currentSelections.shift();
							}
						}
					}

					// Add the new selection
					this.character.details[rank].features.push(featureOption);

				} else {
					// Apply legacy selection rules for non-stat types
					if (rules.num !== undefined) {
						// Exact number required - FIFO replacement
						if (currentSelections.length >= rules.num) {
							// Remove the oldest selection (first in array)
							const oldestIndex = this.character.details[rank].features.findIndex(
								(f: FeatureOption) => f.type === featureOption.type
							);
							this.character.details[rank].features.splice(oldestIndex, 1);
						}
						this.character.details[rank].features.push(featureOption);

					} else if (rules.max !== undefined) {
						// Maximum limit - FIFO replacement when at max
						if (currentSelections.length >= rules.max) {
							// Remove the oldest selection (first in array)
							const oldestIndex = this.character.details[rank].features.findIndex(
								(f: FeatureOption) => f.type === featureOption.type
							);
							this.character.details[rank].features.splice(oldestIndex, 1);
						}
						this.character.details[rank].features.push(featureOption);

					} else if (rules.min !== undefined) {
						// Minimum requirement - allow multiple selections up to a reasonable limit
						this.character.details[rank].features.push(featureOption);

					} else {
						// Default single selection (backward compatibility)
						const index = this.character.details[rank].features.findIndex(
							(f: FeatureOption) => f.type === featureOption.type
						);
						if (index === -1) {
							this.character.details[rank].features.push(featureOption);
						} else {
							this.character.details[rank].features.splice(index, 1);
							this.character.details[rank].features.push(featureOption);
						}
					}
				}
			}

			// When origin or feats change, refresh the bonus cache (bonusStat/bonusFeat/bonusKnack).
			// Feats can carry bonusStat effects (e.g. Human Adaptable +1 stat point).
			if (featureOption.type === FeatureType.origin || featureOption.type === FeatureType.feat) {
				this.bonusCache = { originId: '', bonusStat: 0, bonusFeat: 0, bonusKnack: 0 };
				this.refreshBonusCache(rank);
			}

			// When core changes, clear all core abilities across all ranks
			if (featureOption.type === FeatureType.core) {
				this.character.details = this.character.details.map(detail => ({
					...detail,
					features: detail.features.filter((f: FeatureOption) => f.type !== FeatureType.coreAbility),
				}));
				console.log(`[CharacterStore] Cleared all core abilities — core changed to ${featureOption.name}`);
			}

			// When legacy changes, clear dependent sub-feature selections at this rank
			// (knack, quirk, skillBoon are all gated by legacy tags)
			if (featureOption.type === FeatureType.legacy) {
				const subFeatureTypes: FeatureType[] = [
					FeatureType.knack,
					FeatureType.quirk,
					FeatureType.skillBoon,
					FeatureType.legacyItem,
				];
				this.character.details[rank].features = this.character.details[rank].features.filter(
					(f: FeatureOption) => !subFeatureTypes.includes(f.type)
				);
				console.log(`[CharacterStore] Cleared legacy sub-selections (knack, quirk, skillBoon) at rank ${rank}`);

				// Refresh features store options if currently viewing a tag-dependent type
				const featuresStore = useFeaturesStore();
				if (featuresStore.selectedFeature) {
					const tagDependentTypes: FeatureType[] = [
						FeatureType.knack,
						FeatureType.quirk,
						FeatureType.skillBoon,
						FeatureType.feat,
						FeatureType.talent,
					];
					if (tagDependentTypes.includes(featuresStore.selectedFeature.type)) {
						featuresStore.selectFeature(featuresStore.selectedFeature);
					}
				}
			}

			await this.updatePropertiesFromFeatureList(rank);
		},
		async updatePropertiesFromFeatureList(rank: number) {
			const propertyStore = usePropertyStore();
			let featuresToRemove: FeatureOption[] = [];

			// Build character tags and threshold context for requires checking
			const characterTags = this.characterTags;
			const requiresContext = this.buildRequiresContext();

			// Validate all features - check requires tag cloud + threshold requires
			for (const feature of this.character.details[rank].features) {
				const property = await propertyStore.propertyById(feature.id);
				if (property) {
					if (!meetsRequires(property.requires, characterTags, requiresContext)) {
						console.log(`[CharacterStore] Feature ${feature.name} disqualified - requires not met`);
						featuresToRemove.push(feature);
					}
				} else {
					console.warn(`Property not found for feature ${feature.id}`);
					featuresToRemove.push(feature);
				}
			}

			// Remove disqualified features
			for (const feature of featuresToRemove) {
				const index = this.character.details[rank].features.findIndex((item) => item.id === feature.id);
				if (index !== -1) {
					console.log(`[CharacterStore] featureOption removed ${feature.id}`);
					this.character.details[rank].features.splice(index, 1);
				}
			}

			// Build properties list from features by fetching each property by ID
			this.character.details[rank].properties = [];
			for (const feature of this.character.details[rank].features) {
				const property = await propertyStore.propertyById(feature.id);
				if (property) {
					// Effects will be loaded lazily when needed (e.g., during save or character sheet display)
					// No need to load here - they're already loaded for requirement checks above if needed
					this.character.details[rank].properties.push(property);
				}
			}
		},
		/**
		 * Thin wrapper around the framework-free validator in
		 * composables/characterValidator.ts. Kept as an action so existing
		 * callers (isCharacterValid, getFeatureValidationState, etc.)
		 * don't need to change.
		 */
		validateCharacter(): ValidationError[] {
			const blueprintStore = useBlueprintStore();
			return validateCharacterPure(
				this.character as unknown as Parameters<typeof validateCharacterPure>[0],
				blueprintStore.character as unknown as Parameters<typeof validateCharacterPure>[1],
			);
		}
	}
});
